import { countedKey } from "@/lib/i18n/plural";
import { BellRing, ChefHat } from "lucide-react";
import { Button } from "@a2z/ui";
import { useTranslate } from "@/lib/i18n";
import { useMoney } from "@/shared/ui";
import {
  tillPrimaryBusyKey,
  type CloseUnblockKind,
  type TillClose,
  type TillPrimary,
} from "@/modules/pos/lib/tillActions";
import { CloseBlockedNote, closeBlockedReasonId, useCloseBlockedText } from "./CloseBlockedNote";

/**
 * The till's phone shape, below `lg` (QA §6, round 10).
 *
 * At 390px the menu and the bill cannot both be on screen — a 420px order
 * column beside a menu grid does not exist at that width — so only one pane
 * renders and this bar is the other one's permanent handle. It carries the
 * three things a cashier must never have to go looking for: what the table
 * owes, how many plates that is, and the one act the screen wants next.
 *
 * IT IS NOT `position: sticky`, deliberately. The till shell is a `100svh`
 * column (`.app-shell`) whose panes own their own scrolling, so this bar is a
 * `shrink-0` FLEX SIBLING of the pane row rather than an overlay: it cannot
 * cover the last rows of a list, and no scroll container needs a padding-bottom
 * to compensate for it. A sticky bar would have needed both, and on iOS the
 * bottom of a `100vh` sticky element is the part that hides under the browser
 * chrome — the exact bug `styles.css` writes `svh` to avoid.
 *
 * The whole summary is the switch. Tapping it moves between the menu and the
 * bill, so neither pane is ever more than one tap away, and the label names
 * where the tap GOES rather than where the reader already is.
 *
 * ## THE CLOSE IS A SECOND CONTROL, NOT A SECOND FACE OF THE FIRST (UX-round D15, X2)
 * «UX-round Dn» = the 2026-09-17 UX round's own numbering (`.roundtable/2026-09-17-restaurant-ux/decisions.md`), not §11's.
 *
 * The bar used to carry ONE button whose identity came from `tillPrimaryAction`
 * — and that function answered `fire` or `close` for the same OPEN bill
 * depending on whether the basket was empty. Firing a round emptied it, so
 * «اقفل الفاتورة» appeared in the rectangle the thumb had just left, one gap
 * away from «افتح الحساب», and it closed the bill with no dialog behind it.
 *
 * Now Fire owns the primary slot for the life of OPEN and Close is its own
 * control beside it: different variant, different weight, its own gate
 * (`tillCloseAction`), and a reversal in the toast it raises rather than a
 * confirm in front of it. The two are never the same pixels, so the slot's
 * meaning cannot move under a thumb.
 *
 * `env(safe-area-inset-bottom)` is respected because this is now the
 * bottom-most element of the shell, the same reason `@a2z/ui`'s `BottomNav`,
 * `Drawer` and `ModalSheet` do it. `max()` and not `+`: on a device with no
 * home indicator the inset is 0 and the bar keeps its own padding.
 */
export function TillSummaryBar({
  pane,
  onTogglePane,
  runningTotal,
  lineCount,
  primary,
  close,
  isBusy,
  busyKey,
  onPrimary,
  onClose,
  closeAs = "close",
  handOver = false,
  dispatchFoot,
  placeName,
  closeUnblock = null,
  onCloseUnblock,
  unsentCount = 0,
  onBackToFloor,
}: {
  /** Which pane is on screen at narrow widths. */
  pane: "menu" | "bill";
  onTogglePane: () => void;
  runningTotal: number;
  lineCount: number;
  /** The primary act, from `tillPrimaryAction` — one kind per status (X2). */
  primary: TillPrimary;
  /** The close, from `tillCloseAction`. `null` on a bill past OPEN. */
  close: TillClose | null;
  isBusy: boolean;
  busyKey: string | null;
  onPrimary: () => void;
  onClose: () => void;
  /**
   * PAPER (2026-10-03): where the wide panel's foot offers Settle in Close's
   * place, this bar offers the SAME act — one person, one bill, one answer at
   * every width, the caller's Settle in `onClose`. The dark till's direct
   * takeaway uses it too (founder, 2026-10-08). As Settle the caller passes a
   * `close` that does not wait for the kitchen (D126), as the foot's Settle
   * does not; as Close it carries the D22 gate.
   */
  closeAs?: "close" | "settle";
  /** The close hands the bag to its accepted driver: «تسليم إلى المندوب» (OrderPanel's `handOver`). */
  handOver?: boolean;
  /** OrderPanel's `dispatchFoot`: «إسناد إلى مندوب» takes the primary while nothing waits to be sent. */
  dispatchFoot?: {
    /**
     * "assign": nobody holds the bag — the close's slot is «إسناد إلى مندوب»
     * (the Delivery tab's panel) and the close a small secondary beside it,
     * for a bag leaving without an app driver (typed rider, the customer).
     * "offered": an offer is out — no primary; the head's chip counts down,
     * and the small close stays.
     */
    kind: "assign" | "offered";
    onAssign: () => void;
    assignDisabled: boolean;
    /** Why the assign is greyed, said under it (null: the head already says it). */
    assignReason: string | null;
    /** The id of the line that says why (the head's contact line, or this one). */
    describedBy?: string;
    /** The greyed assign's tooltip: why, in words. */
    assignTitle?: string | null;
  };
  /**
   * Which table this is, beside "Bill" (founder, 2026-10-06). On a phone the
   * register drops its breadcrumb, and this card is then the one place on
   * screen that says whose order the waiter is adding to.
   */
  placeName?: string | null;
  /** T6 / D173 — the one tap beside a greyed Close (see `CloseBlockedNote`). */
  closeUnblock?: CloseUnblockKind | null;
  onCloseUnblock?: () => void;
  /**
   * Plates not sent yet (waiter phone, 2026-10-28). Said beside the total —
   * «2 لم تُرسل» — so the bar answers "did it go?" on both panes.
   */
  unsentCount?: number;
  /**
   * «رجوع إلى الصالة» (waiter phone, 2026-10-28): with nothing left to send
   * and no close of their own, the next act for a waiter is the floor. Shown
   * in the primary's place only then; a cashier's bar is unchanged.
   */
  onBackToFloor?: () => void;
}) {
  const t = useTranslate();
  const money = useMoney();
  /* 2026-10-20 — a greyed close says why in its tooltip and description, not a line. */
  /* 2026-10-20 — «طباعة التذكرة» is behind the bill's printer: the reason
     points there, and the pane toggle (the way to the bill) wears the dot. */
  const ticketOnPrinter = closeUnblock === "resendTicket";
  const closeReasonText = useCloseBlockedText(close?.reason ?? null, handOver, ticketOnPrinter);
  const toggleAttention = pane === "menu" && ticketOnPrinter && Boolean(close?.reason);
  const closeReasonTitle =
    close && !close.enabled && closeAs !== "settle" && closeReasonText ? closeReasonText : undefined;

  const primaryLabel = {
    fire: t((m) => m.pos.sendToKitchen),
    requestCashier: t((m) => m.pos.sendToCashier),
    settle: t((m) => m.pos.settle),
    newOrder: t((m) => m.pos.newOrder),
  }[primary.kind];
  const primaryBusyKey = tillPrimaryBusyKey(primary.kind);
  /* 2026-10-20 (re-gate): a driver accepted and nothing is left to send — the
     hand-over IS the next act, so it takes the primary slot and its fill. */
  const handOverPrimary =
    handOver && close !== null && closeAs !== "settle" && !(primary.permitted && primary.enabled);
  const sendKind = primary.kind === "fire" || primary.kind === "requestCashier";
  /* The bill pane shows only Send, and only while there is something to send. */
  const showPrimaryOnBill =
    pane === "bill" && sendKind && primary.permitted && primary.enabled && !dispatchFoot;
  /* Sent everything, cannot close: the floor is next (a waiter). */
  const backToFloor =
    onBackToFloor !== undefined &&
    sendKind &&
    !primary.enabled &&
    unsentCount === 0 &&
    close === null &&
    !dispatchFoot;

  return (
    <div
      // `lg:hidden`: above 1024px both panes are on screen and the panel's own
      // pinned foot carries the totals and the actions. This bar exists only
      // for the width where that foot can be off-screen.
      // `flex-wrap`: with a second control in the row, 390px cannot always
      // hold the summary and both buttons on one line — and the panel's own
      // foot documents what happens when a row like this is left unwrapped
      // (a primary painted 104px outside the window). Wrapping puts the
      // actions on their own full-width line instead, which also pushes Close
      // and Fire further apart rather than closer together.
      // Slim (waiter phone, 2026-10-28: "the bottom part is taking a lot of
      // space"): one row — the switch on one line, the act beside it — wrapping
      // only where a portrait phone cannot hold both.
      className="flex shrink-0 flex-wrap items-center gap-2 border-t border-[color:var(--border-light)] bg-[color:var(--surface)] px-3 pt-2 pb-[max(0.5rem,env(safe-area-inset-bottom))] lg:hidden"
      data-testid="till-summary-bar"
    >
      <button
        type="button"
        onClick={onTogglePane}
        data-testid="till-pane-toggle"
        data-attention={toggleAttention ? "" : undefined}
        aria-describedby={
          toggleAttention && closeAs !== "settle" && close && !close.enabled
            ? closeBlockedReasonId("bar")
            : undefined
        }
        title={toggleAttention ? (closeReasonText ?? undefined) : undefined}
        data-ui="select"
        // A row of its own on a phone (`basis-full`): sharing one with both
        // actions at 430px squeezed the total onto two lines and cut the
        // line count to "… أص" (founder, 2026-10-06).
        className="relative flex min-h-[44px] min-w-0 flex-1 basis-[14rem] items-center gap-2 rounded-[var(--radius-list)] border border-[color:var(--border-strong)] bg-[color:var(--pos-track)] px-3 py-1 text-start transition-colors hover:border-[color:var(--text-disabled)]"
      >
        {toggleAttention ? (
          <span
            aria-hidden="true"
            data-testid="till-pane-toggle-dot"
            className="pointer-events-none absolute -top-1 -end-1 h-2.5 w-2.5 rounded-full bg-[color:var(--warning)] ring-2 ring-[color:var(--surface)]"
          />
        ) : null}
        {/* ONE line (2026-10-28): where the tap goes, the total, the count. */}
        <span className="flex min-w-0 flex-1 items-baseline gap-2 whitespace-nowrap">
          <span className="shrink-0 text-[length:var(--font-size-2xs)] font-extrabold uppercase tracking-[0.06em] text-[color:var(--text-muted)]">
            {pane === "menu"
              ? t((m) => m.pos.tillShowBill)
              : t((m) => m.pos.tillShowMenu)}
            {placeName ? (
              <span className="normal-case tracking-normal" data-testid="till-summary-place">
                {" · "}
                <bdi>{placeName}</bdi>
              </span>
            ) : null}
          </span>
          <span className="flex min-w-0 items-baseline gap-1.5">
            <span
              className="shrink-0 text-[length:var(--font-size-md)] font-extrabold tabular-nums"
              data-testid="till-summary-total"
            >
              {money(runningTotal)}
            </span>
            <span className="truncate text-[length:var(--font-size-2xs)] font-semibold text-[color:var(--text-muted)]">
              {t(
  // B117/A2 — the DUAL. `plural.ts` holds the rule; this call site
  // was still spelling one form for every number.
  countedKey(lineCount, {
    one: (m) => m.pos.lineCountOne,
    two: (m) => m.pos.lineCountTwo,
    few: (m) => m.pos.lineCountFew,
    many: (m) => m.pos.lineCount,
  }),
  { count: lineCount },
)}
              {unsentCount > 0 ? (
                <span data-testid="till-summary-unsent">
                  {" · "}
                  {t(
                    countedKey(unsentCount, {
                      one: (m) => m.pos.tillUnsentCountOne,
                      two: (m) => m.pos.tillUnsentCountTwo,
                      few: (m) => m.pos.tillUnsentCountFew,
                      many: (m) => m.pos.tillUnsentCount,
                    }),
                    { count: unsentCount },
                  )}
                </span>
              ) : null}
            </span>
          </span>
        </span>
        {/* Points at the start-to-end direction — "go there" — and is mirrored
            under RTL by the transform, not by swapping the glyph, exactly as
            the screen's back arrow is. */}
        <svg
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.2"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
          className="shrink-0 text-[color:var(--text-muted)] rtl:-scale-x-100"
        >
          <path d="m9 18 6-6-6-6" />
        </svg>
      </button>

      {/* The actions ride here ONLY while the menu is the pane on screen. On
          the bill pane the panel's own foot is a few pixels above this bar with
          the same acts plus everything that qualifies them — the close-blocked
          sentence, the reopen, the print — and two identical primary buttons
          stacked 60px apart is a worse answer than one in its context.

          They wrap as a PAIR (their own flex row), so a narrow screen moves
          both onto the second line together rather than orphaning one of
          them wherever the wrap happens to land.

          ORDER, and it is not cosmetic: primary at the inline START, close
          after it — the same order the panel's own foot lays them out in, so
          the two surfaces do not teach a thumb two different places for the
          same act. In Arabic that puts Send under the right thumb and the
          close the width of a button further away, which is the right way
          round for the one that ends a bill. */}
      {/* 2026-10-28 (waiter phone): Send rides here on BOTH panes — opening
          the bill to check it used to take Send off the screen whenever the
          panel's foot was below the fold (a phone on its side). The panel's
          own Send is hidden below `lg` for the same reason (OrderPanel), so
          there is still one. Close and its reason stay with the menu pane:
          on the bill pane the foot carries them with what qualifies them. */}
      {backToFloor ? (
        <div className="flex min-w-0 flex-1 basis-[11rem] items-center justify-end gap-2 sm:flex-none sm:basis-[17.5rem]">
          <Button
            variant="primary"
            size="touch"
            className="flex-1 shrink-0 rounded-[var(--radius-list)] font-extrabold"
            data-testid="till-summary-floor"
            onClick={onBackToFloor}
          >
            {t((m) => m.pos.tillBackToFloor)}
          </Button>
        </div>
      ) : pane === "menu" || showPrimaryOnBill ? (
        /* Send alone keeps the design's 280px beside the switch; with a close
           beside it the pair shares the row as before. */
        <div
          className={`flex min-w-0 flex-1 basis-[11rem] items-center justify-end gap-2${
            close && pane === "menu" ? "" : " sm:flex-none sm:basis-[17.5rem]"
          }`}
        >
          {/* Not offered without its grant (founder, 2026-10-06). */}
          {handOverPrimary ? null : dispatchFoot && !(primary.permitted && primary.enabled) ? (
            dispatchFoot.kind === "assign" ? (
              <Button
                variant="primary"
                size="touch"
                className="flex-[1.3] shrink-0 rounded-[var(--radius-list)] font-extrabold"
                disabled={dispatchFoot.assignDisabled || isBusy}
                aria-describedby={dispatchFoot.assignDisabled ? "till-summary-assign-reason" : undefined}
                title={dispatchFoot.assignDisabled ? (dispatchFoot.assignTitle ?? undefined) : undefined}
                data-testid="till-summary-assign"
                onClick={dispatchFoot.onAssign}
              >
                {t((m) => m.dispatch.actionAssign)}
              </Button>
            ) : null
          ) : primary.permitted ? (
          <Button
            variant="primary"
            size="touch"
            className="flex-[1.3] shrink-0 rounded-[var(--radius-list)] font-extrabold"
            disabled={!primary.enabled || isBusy}
            isLoading={primaryBusyKey !== null && busyKey === primaryBusyKey}
            data-testid="till-summary-primary"
            onClick={onPrimary}
            leadingIcon={
              primary.kind === "fire" ? (
                <ChefHat size={17} strokeWidth={2} aria-hidden="true" />
              ) : primary.kind === "requestCashier" ? (
                <BellRing size={17} strokeWidth={2} aria-hidden="true" />
              ) : undefined
            }
          >
            {primaryLabel}
            {/* How many plates it sends (the design's pill on the button). */}
            {sendKind && unsentCount > 0 ? (
              <span
                data-testid="till-summary-primary-count"
                className="ms-2 inline-flex h-[22px] min-w-[22px] items-center justify-center rounded-full bg-white/25 px-1.5 text-[length:var(--font-size-xs)] tabular-nums"
              >
                {unsentCount}
              </span>
            ) : null}
          </Button>
          ) : null}
          {/* CLOSE, THE SECONDARY (UX-round D15). Not a primary anywhere: `outline`
              against the primary's fill, a fixed place of its own, and the
              D22 gate it has always had — read from `tillCloseAction`, which
              is the only copy of that rule.

              No confirm in front of it (UX-round D17): closing prints nothing, tells
              nobody and moves no money, so the three-question test lands on
              "no dialog, ship an undo" — and the undo is in the toast the
              caller raises, for a window the caller states. */}
          {close && pane === "menu" ? (
            <Button
              variant={handOverPrimary ? "primary" : dispatchFoot ? "ghost" : "outline"}
              size="touch"
              className={
                handOverPrimary
                  ? "flex-1 rounded-[var(--radius-list)] font-extrabold"
                  : dispatchFoot
                    ? "shrink-0 rounded-[var(--radius-list)] font-bold"
                    : "flex-1 rounded-[var(--radius-list)] font-bold"
              }
              disabled={!close.enabled || isBusy}
              isLoading={
                busyKey === tillPrimaryBusyKey("close") ||
                (closeAs === "settle" && busyKey === "settle")
              }
              title={closeReasonTitle}
              aria-describedby={closeReasonTitle ? closeBlockedReasonId("bar") : undefined}
              data-testid={closeAs === "settle" ? "till-summary-settle" : "till-summary-close"}
              onClick={onClose}
            >
              {closeAs === "settle"
                ? t((m) => m.pos.settle)
                : handOver
                  ? t((m) => m.dispatch.handOverToDriver)
                  : t((m) => m.pos.close)}
            </Button>
          ) : null}
        </div>
      ) : null}
      {/* T6 / D173 — under the greyed Close, the reason and its one tap: the
          same component as the panel's foot, so a phone reads the same words. */}
      {pane === "menu" && close && !close.enabled && close.reason && closeAs !== "settle" ? (
        <CloseBlockedNote
          reason={close.reason}
          action={closeUnblock}
          onAction={onCloseUnblock}
          isBusy={isBusy}
          isLoading={busyKey === "close-unblock"}
          surface="bar"
          handOver={handOver}
        />
      ) : null}
      {/* The bill's head is off-screen on this pane: the greyed assign says why in its
          tooltip and this hidden description (2026-10-20: no line under the button). */}
      {pane === "menu" && dispatchFoot?.kind === "assign" && dispatchFoot.assignDisabled ? (
        <p
          id="till-summary-assign-reason"
          role="status"
          data-testid="till-summary-assign-reason"
          className="sr-only"
        >
          {dispatchFoot.assignReason ?? t((m) => m.dispatch.contactMissing)}
        </p>
      ) : null}
    </div>
  );
}
